import React, { useEffect, useState } from 'react';
import { ExternalLink, AlertCircle, RefreshCw, BarChart2, LineChart, PieChart } from 'lucide-react';
import { GoogleAd } from '../types/dataTypes';
import { fetchGoogleAds } from '../lib/supabase';

interface GoogleAdsTableDisplayProps {
  companyId: string;
}

const GoogleAdsTableDisplay: React.FC<GoogleAdsTableDisplayProps> = ({ companyId }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [ads, setAds] = useState<GoogleAd[]>([]);

  const loadAds = async () => {
    console.log('Loading Google Ads for company ID:', companyId);
    setIsLoading(true);
    setError(null);
    
    try {
      const googleAds = await fetchGoogleAds(companyId);
      console.log('Fetched Google Ads:', googleAds);
      setAds(googleAds);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error loading Google Ads:', err);
      setError(err instanceof Error ? err.message : 'Failed to load ads data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAds();
  }, [companyId]);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mt-8">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mt-8">
        <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-2" />
            <div>
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="google-ads" className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mt-8">
      <div className="relative bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg shadow-xl overflow-hidden p-6 mb-8">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-4 left-4 text-white">
            <BarChart2 size={120} />
          </div>
          <div className="absolute bottom-4 right-4 text-white">
            <LineChart size={120} />
          </div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white">
            <PieChart size={160} />
          </div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,_rgba(255,255,255,0.2)_0%,_transparent_100%)]"></div>
        </div>
        <div className="relative z-10">
          <h2 className="text-xl font-semibold text-white mb-2">
            Google Ads Intelligence
          </h2>
          <p className="text-blue-100">
            Analyze your competitors' Google ad campaigns. Uncover creative strategies, messaging themes, and targeting insights to optimize your search and display advertising.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          🎯 Your Competitors Google Ads
        </h2>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {ads.length} ads found
          </span>
        </div>
      </div>
      
      {ads.length === 0 ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 p-4">
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            No Google ads found for company ID: {companyId}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Preview
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Headline
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Advertiser
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Region
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {ads.map((ad) => (
                <tr key={ad.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4">
                    {ad.previewUrl ? (
                      <div className="relative group">
                        <div className="w-48 h-32 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                          <div className="p-3 text-xs">
                            <div className="font-medium text-gray-900 dark:text-gray-100 mb-1 line-clamp-2">
                              {ad.headline}
                            </div>
                            <div className="text-gray-600 dark:text-gray-300 line-clamp-3">
                              {ad.description}
                            </div>
                          </div>
                        </div>
                        <a 
                          href={ad.previewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 rounded-lg transition-opacity duration-200"
                        >
                          <ExternalLink className="text-white" size={16} />
                        </a>
                      </div>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">No preview</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                    {ad.headline}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                    {ad.description}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                    {ad.advertiserName}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                    {ad.creative_region}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GoogleAdsTableDisplay;